import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

const q = (p: Record<string, unknown>) => new URLSearchParams(
  Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== '').reduce((a, [k, v]) => ({ ...a, [k]: String(v) }), {})
).toString()

export const bookingService = {
  list: (p: {
    page?: number; limit?: number; search?: string; booking_status?: string
    payment_status?: string; payment_method?: string; start_date?: string; end_date?: string; driver_id?: number
  } = {}) => {
    const params: Record<string, unknown> = { page: p.page ?? 1, limit: p.limit ?? 15 }
    if (p.search) params.search = p.search
    if (p.booking_status && p.booking_status !== 'all') params.booking_status = p.booking_status
    if (p.payment_status) params.payment_status = p.payment_status
    if (p.payment_method) params.payment_method = p.payment_method
    if (p.start_date) params.start_date = p.start_date
    if (p.end_date) params.end_date = p.end_date
    if (p.driver_id) params.driver_id = p.driver_id
    return restClient.get(`${endpoints.booking.getList}?${q(params)}`).then(r => r.data)
  },
  listSchoolAdmin: (p: Record<string, unknown> = {}) => {
    const params: Record<string, unknown> = { page: p.page ?? 1, limit: p.limit ?? 15, ...p }
    return restClient.get(`${endpoints.booking.schoolAdmin.getList}?${q(params)}`).then(r => r.data)
  },
  getById: (id: number, isSchool = false) =>
    restClient.get(`${isSchool ? endpoints.booking.schoolAdmin.getById : endpoints.booking.getById}?id=${id}`).then(r => r.data),
  updateStatus: (data: Record<string, unknown>) =>
    restClient.post(endpoints.booking.updateStatus, data).then(r => r.data),
  sessionRequests: (p: { page?: number; limit?: number } = {}) => {
    const params: Record<string, unknown> = { page: p.page ?? 1, limit: p.limit ?? 15 }
    return restClient.get(`${endpoints.booking.sessionRequests}?${q(params)}`).then(r => r.data)
  },
  // POST update-session-status-on-behalf { sessions_id, session_status, driver_comments? }
  // session_status: 1 = accepted, 2 = rejected
  updateSessionStatusOnBehalf: (data: Record<string, unknown>) =>
    restClient.post(endpoints.booking.updateSessionStatusOnBehalf, data).then(r => r.data),
  // POST session/edit — reschedule / restatus a single session
  // { session_id, start_time: 'YYYY-MM-DD HH:mm:ss', end_time, session_status: number, reason }
  editSession: (data: Record<string, unknown>) =>
    restClient.post(endpoints.booking.sessionEdit, data).then(r => r.data),
  // GET session/edit-history?session_id= → audit log of past edits to one session
  sessionEditHistory: (session_id: number) =>
    restClient.get(`${endpoints.booking.sessionEditHistory}?${q({ session_id })}`).then(r => r.data),
  // GET enum/get-session-status-type-enum → { values: [{ name, value }] }
  sessionStatusEnum: () =>
    restClient.get(endpoints.enum.sessionStatus).then(r => r.data),
  // GET enum/get-payment-status-type-enum → { values: [{ name, value }] }
  paymentStatusEnum: () =>
    restClient.get(endpoints.booking.getPaymentStatus).then(r => r.data),
  // POST trainer-changing/change-free { booking_id, new_trainer_id, reason }
  changeTrainer: (data: Record<string, unknown>) =>
    restClient.post(endpoints.booking.changeTrainer, data).then(r => r.data),
  // GET trainer-changing/get-trainer-list?booking_id= — eligible replacement trainers for a booking
  changeTrainerList: (booking_id: number) =>
    restClient.get(`${endpoints.booking.changeTrainerList}?${q({ booking_id })}`).then(r => r.data),
  // GET trainer-swap-history/get-list?page=&limit= — audit log of past trainer changes
  trainerSwapHistory: (p: { page?: number; limit?: number } = {}) => {
    const params: Record<string, unknown> = { page: p.page ?? 1, limit: p.limit ?? 20 }
    return restClient.get(`${endpoints.booking.trainerSwapHistory}?${q(params)}`).then(r => r.data)
  },
  // ── Refunds ───────────────────────────────────────────────────────────────
  // Refund request list (pending / approved / rejected). Mirrors the old
  // useGetRefundRequestList: supports city_id, category_id, driver_id, search
  // and a repeatable `status[]` query param.
  refundList: (p: {
    page?: number; limit?: number; search?: string; status?: string | string[]
    city_id?: number | string; category_id?: number | string; driver_id?: number | string
  } = {}) => {
    const params = new URLSearchParams()
    params.append('page', String(p.page ?? 1))
    params.append('limit', String(p.limit ?? 15))
    if (p.search) params.append('search', p.search)
    if (p.city_id != null && p.city_id !== '') params.append('city_id', String(p.city_id))
    if (p.category_id != null && p.category_id !== '') params.append('category_id', String(p.category_id))
    if (p.driver_id != null && p.driver_id !== '') params.append('driver_id', String(p.driver_id))
    if (Array.isArray(p.status)) p.status.forEach(s => params.append('status[]', s))
    else if (p.status) params.append('status[]', p.status)
    return restClient.get(`${endpoints.booking.refund.list}?${params}`).then(r => r.data)
  },
  // Already-refunded list (no status filter). Mirrors old useGetRefundedList.
  refundedList: (p: {
    page?: number; limit?: number
    city_id?: number | string; category_id?: number | string; driver_id?: number | string
  } = {}) => {
    const params = new URLSearchParams()
    params.append('page', String(p.page ?? 1))
    params.append('limit', String(p.limit ?? 15))
    if (p.city_id != null && p.city_id !== '') params.append('city_id', String(p.city_id))
    if (p.category_id != null && p.category_id !== '') params.append('category_id', String(p.category_id))
    if (p.driver_id != null && p.driver_id !== '') params.append('driver_id', String(p.driver_id))
    return restClient.get(`${endpoints.booking.refund.refundedList}?${params}`).then(r => r.data)
  },
  refundById: (bookingId: number) =>
    restClient.get(`${endpoints.booking.refund.getById}?booking_id=${bookingId}`).then(r => r.data),
  // POST update-status-by-booking-id → process the refund amount { booking_id, amount_refunded }
  updateRefundStatus: (data: Record<string, unknown>) =>
    restClient.post(endpoints.booking.refund.update, data).then(r => r.data),
  // POST update-refund-request → change request status { booking_id, status }
  updateRefundRequest: (data: Record<string, unknown>) =>
    restClient.post(endpoints.booking.refund.updateStatus, data).then(r => r.data),
  // POST create-refund-request-by-booking-id { booking_id, reason }
  createRefund: (data: Record<string, unknown>) =>
    restClient.post(endpoints.booking.refund.createRefund, data).then(r => r.data),
  // ── Auto-reassign ─────────────────────────────────────────────────────────
  // GET/POST the same endpoint — POST with a partial payload updates in place
  // and returns the full settings object back.
  getAutoReassignSettings: () =>
    restClient.get(endpoints.booking.autoReassign.settings).then(r => r.data),
  updateAutoReassignSettings: (data: {
    mode?: string; enabled?: boolean; timeout_minutes?: number; max_hops?: number; max_distance_km?: number
  }) => restClient.post(endpoints.booking.autoReassign.settings, data).then(r => r.data),
  // Bookings an auto-reassign attempt couldn't resolve on its own (no nearby
  // trainer free, or the trainer-change limit was hit) and that now need an
  // admin to step in. City-scoped admins only ever see their own city's issues.
  listAutoReassignIssues: (p: {
    page?: number; limit?: number; status?: string; reason?: string; booking_id?: number; search?: string
  } = {}) => {
    const params: Record<string, unknown> = { page: p.page ?? 1, limit: p.limit ?? 20 }
    if (p.status)     params.status = p.status
    if (p.reason)     params.reason = p.reason
    if (p.booking_id) params.booking_id = p.booking_id
    if (p.search)     params.search = p.search
    return restClient.get(`${endpoints.booking.autoReassign.issuesList}?${q(params)}`).then(r => r.data)
  },
  // GET .../issues/count → { open: 3 } — the sidebar badge.
  countAutoReassignIssues: () =>
    restClient.get(endpoints.booking.autoReassign.issuesCount).then(r => r.data),
  // POST .../issues/resolve { booking_id, note? } — 404 if the booking has no
  // open issue, 409 if another admin already resolved it in the meantime.
  resolveAutoReassignIssue: (data: { booking_id: number; note?: string }) =>
    restClient.post(endpoints.booking.autoReassign.issuesResolve, data).then(r => r.data),
  // Every step of every auto-reassign attempt — a booking can have several rows
  // (one per hop) if it bounced between trainers before settling or escalating.
  // School admins only see rows involving their own school's trainers.
  listAutoReassignHistory: (p: {
    page?: number; limit?: number; booking_id?: number; trainer_id?: number; reason?: string
  } = {}) => {
    const params: Record<string, unknown> = { page: p.page ?? 1, limit: p.limit ?? 20 }
    if (p.booking_id) params.booking_id = p.booking_id
    if (p.trainer_id) params.trainer_id = p.trainer_id
    if (p.reason)     params.reason = p.reason
    return restClient.get(`${endpoints.booking.autoReassign.historyList}?${q(params)}`).then(r => r.data)
  },
}
